import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import InfiniteScroll from 'react-infinite-scroll-component'
import { flatten, floor } from 'lodash-es'

import {
  useKeywordByUrlId,
  useInfiniteArticles,
  useArticleCount,
  useBanners,
} from 'utils/api/hooks'
import { selectWebview } from 'store/webview/webviewSlice'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import ContentWrapper from 'components/common/ContentWrapper'
import ArticleListItem from 'components/common/ArticleListItem'
import TagButton from 'components/common/TagButton'
import InfiniteContainer from 'components/common/InfiniteContainer'
import ArticleClassifications from 'components/common/ArticleClassifications'
import LatestArticle from 'components/common/LatestArticle'
import { HideInDesktopBox, HideInMobileBox } from 'components/common/Hidden'
import { Solid } from 'components/common/Divider'
import GuessLikesList from 'components/common/GuessLikesList'
import { AdvertisementBanner } from 'components/common/Banner'
import Footer from 'components/common/Footer'

const SearchByTag = ({ fallbackData }) => {
  const router = useRouter()
  const { data: keyword } = useKeywordByUrlId(router.query?.tagUrlId, {
    fallbackData: [fallbackData?.keyword],
  })
  const { data, size, setSize } = useInfiniteArticles(
    () => ({
      keywordId: keyword._id,
    }),
    { fallbackData: [fallbackData?.articles] }
  )
  const articles = flatten(data)
  const webview = useSelector(selectWebview)

  const { data: advertisementBanner } = useBanners(webview?.name, {
    displayAt: 'articleListAdvertisement',
  })
  const bannerList = flatten(advertisementBanner)

  const onLoadMore = () => {
    setSize(size + 1)
  }

  const { data: articleCount } = useArticleCount(
    () => ({
      keywordId: keyword._id,
    }),
    { fallbackData: fallbackData?.articleCount }
  )

  const containerId = 'scrollable-article-container'
  useEffect(() => {
    if (webview?.name === 'lsApp') {
      try {
        enableNativeHeaderMainTabs(false)
        enableNativeHeaderNavBack(true)
        enableNativeHeaderSearch(false)
      } catch (err) {
        console.error('native notifier error: ', err.message)
      }
    }
  }, [webview])

  return (
    <Layout stickyHeader={<Header />} fixContentHeight>
      <InfiniteContainer id={containerId}>
        <ContentWrapper>
          <Typography variant="headlineSmall" mt={8}>
            共{' '}
            <Typography
              variant="headlineSmall"
              color="primary"
              component="span"
            >
              {articleCount}
            </Typography>{' '}
            篇文章有 <TagButton size="lg">{keyword?.name}</TagButton> 標籤
          </Typography>
          <Grid container mt={{ xs: 12, lg: 18 }}>
            <Grid
              item
              xs={12}
              lg={8}
              pr={{ xxl: 12, maxLimit: 24 }}
              px={{ md: 8, lg: 0 }}
            >
              <InfiniteScroll
                dataLength={articles?.length || 0}
                hasMore={true}
                next={onLoadMore}
                scrollableTarget={containerId}
                scrollThreshold={0.6}
              >
                {articles?.map((article, index) => (
                  <Box key={article._id}>
                    <Box mb={{ xs: 8, md: 15 }}>
                      <ArticleListItem article={article} />
                    </Box>
                    {/* 每隔十則文章顯示一個banner */}
                    {(index + 1) % 10 === 0 &&
                    bannerList?.[floor((index + 1) / 10) - 1] ? (
                      <AdvertisementBanner
                        banner={bannerList?.[floor((index + 1) / 10) - 1]}
                      />
                    ) : (
                      ''
                    )}
                    {/* 不足十則也顯示一個banner */}
                    {index === articles?.length - 1 &&
                    (index + 1) % 10 !== 0 &&
                    bannerList?.[floor((index + 1) / 10)] ? (
                      <AdvertisementBanner
                        banner={bannerList?.[floor((index + 1) / 10)]}
                      />
                    ) : (
                      ''
                    )}
                  </Box>
                ))}
                <Solid mt={{ xs: 18, xl: 24 }} />
              </InfiniteScroll>
              <HideInMobileBox>
                <LatestArticle mt={12} />
              </HideInMobileBox>
            </Grid>
            <Grid
              item
              xs={12}
              lg={4}
              px={{ md: 8, lg: 0 }}
              pl={{ lg: 20, xl: 24, xxl: 38, maxLimit: 41 }}
            >
              <ArticleClassifications
                mt={{ xs: 12, lg: 0 }}
              ></ArticleClassifications>
              <GuessLikesList keyword={[keyword._id]} mt={12} />
              <HideInDesktopBox>
                <LatestArticle mt={12} />
              </HideInDesktopBox>
            </Grid>
          </Grid>
        </ContentWrapper>

        {webview?.name !== 'lsApp' && <Footer />}
      </InfiniteContainer>
    </Layout>
  )
}

export default SearchByTag
